// MarketplaceListingCard — Marketplace 2.0 grid tile.
// Spec: Playbook § 5.1 (Decision 1 — image-dominant, whole card is the tap target).
// Demo: marketplace_demo.jsx ListingCard (~lines 450–533).

import React, { useState } from 'react';
import TrustBadge from './TrustBadge';

export default function MarketplaceListingCard({ listing, onOpen }) {
  const [hover, setHover] = useState(false);
  const { seller = {}, statusType, status, set, cardNumber, name, grade, price, imageUrl, serial, parallel, parallelNum } = listing;

  const isSale = statusType === 'sale';
  const pillBg = isSale ? 'rgba(139, 92, 246, 0.9)' : 'rgba(6, 182, 212, 0.85)';
  const pillText = status || (isSale ? 'FOR SALE' : 'FOR TRADE');

  // Parallel / serial line — e.g. "Refractor · 60/99", "60/99", or "Refractor".
  // Serial alone reads as "#60"; with a known print run it becomes "60/99".
  const serialText = serial ? (parallelNum ? `${serial}/${parallelNum}` : `#${serial}`) : null;
  const parallelLine = [parallel, serialText].filter(Boolean).join(' · ') || null;

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpen && onOpen(); } }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      aria-label={`${pillText} · ${set} #${cardNumber} ${name}${price ? ` · $${price}` : ''} · seller ${seller.handle || ''}`}
      style={{
        background: 'var(--panel-2)',
        border: hover ? '0.5px solid rgba(var(--violet-rgb),0.5)' : '0.5px solid rgba(var(--wash-rgb),0.08)',
        borderRadius: 8,
        overflow: 'hidden',
        cursor: 'pointer',
        transform: hover ? 'translateY(-2px)' : 'translateY(0)',
        transition: 'transform 0.15s ease, border-color 0.15s ease, box-shadow 0.15s ease',
        boxShadow: hover ? '0 8px 24px rgba(0,0,0,0.4)' : '0 2px 6px rgba(0,0,0,0.2)',
        userSelect: 'none',
        outline: 'none',
      }}
    >
      {/* Image zone — ~65% of card height per Decision 1 */}
      <div style={{
        aspectRatio: '1 / 1.15',
        position: 'relative',
        background: 'var(--bg-2)',
        overflow: 'hidden',
      }}>
        {imageUrl ? (
          <img
            src={imageUrl}
            alt={name || 'card'}
            loading="lazy"
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'contain',
              display: 'block',
            }}
          />
        ) : (
          <div style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontFamily: "'Share Tech Mono', monospace",
            fontSize: 9,
            letterSpacing: '2px',
            color: 'var(--border-2)',
          }}>NO PHOTO</div>
        )}

        {/* Status pill — absolute, top-left, backdrop blur */}
        <div style={{
          position: 'absolute',
          top: 8,
          left: 8,
          fontSize: 9,
          padding: '3px 7px',
          background: pillBg,
          color: 'white',
          borderRadius: 3,
          fontWeight: 500,
          letterSpacing: '0.04em',
          backdropFilter: 'blur(8px)',
          WebkitBackdropFilter: 'blur(8px)',
          fontFamily: "'JetBrains Mono', monospace",
        }}>{pillText}</div>
      </div>

      {/* Info zone — ~35% of card height */}
      <div style={{ padding: '10px 10px 9px' }}>
        {/* Price — doubled visual weight, amber, JetBrains Mono */}
        {price ? (
          <div style={{
            fontSize: 18,
            fontWeight: 500,
            color: 'var(--warn)',
            lineHeight: 1,
            marginBottom: 6,
            fontFamily: "'JetBrains Mono', monospace",
          }}>${price}</div>
        ) : (
          <div style={{
            fontSize: 14,
            fontWeight: 500,
            color: 'var(--teal)',
            lineHeight: 1,
            marginBottom: 6,
            fontFamily: "'JetBrains Mono', monospace",
          }}>Trade only</div>
        )}

        {/* Card identity */}
        <div style={{
          fontSize: 10,
          color: 'var(--muted-5)',
          lineHeight: 1.3,
          marginBottom: 8,
          fontFamily: "'JetBrains Mono', monospace",
        }}>
          <div style={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}>{set} · #{cardNumber}</div>
          <div style={{
            color: 'var(--text-bright)',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}>{name}{grade ? ` · ${grade}` : ''}</div>
          {parallelLine && (
            <div style={{
              color: 'var(--peach)',
              marginTop: 3,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}>{parallelLine}</div>
          )}
        </div>

        {/* Seller block — sm TrustBadge + handle + verified ✓ */}
        <div style={{
          borderTop: '0.5px solid rgba(var(--wash-rgb),0.06)',
          paddingTop: 8,
          display: 'flex',
          alignItems: 'center',
          gap: 6,
        }}>
          {seller.sellerColor && (
            <TrustBadge
              color={seller.sellerColor}
              count={seller.sellerCount}
              role="seller"
              size="sm"
              interactive={false}
            />
          )}
          <span style={{
            fontSize: 10,
            color: 'var(--violet)',
            fontFamily: "'JetBrains Mono', monospace",
            flex: 1,
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}>{seller.handle}</span>
          {seller.verified && (
            <span
              aria-label="identity verified"
              title="Identity verified"
              style={{ fontSize: 11, color: 'var(--ok)', lineHeight: 1 }}
            >✓</span>
          )}
        </div>
      </div>
    </div>
  );
}
